import * as React from "react"

import { cn } from "@/lib/utils"

// Phase 280d — Scroll-aware symmetric edge-fade affordance.
//
// • Mobile only (< 640px).  Desktop tables are never masked — full visibility
//   for the action column on the far right.
// • Left fade appears the moment the user scrolls right (scrollLeft > 0),
//   signalling "and you can swipe back".  Right fade hides the moment the
//   user has scrolled all the way to the end (scrollLeft + clientWidth ≥
//   scrollWidth), so the rightmost action button is never clipped.
// • If the table has no horizontal overflow at all (fits the viewport),
//   no mask is applied — a static narrow table doesn't need the affordance.
// • Uses ResizeObserver on the wrapper AND the inner <table> so dynamic
//   content (row additions, filter changes) triggers a re-eval.
const Table = React.forwardRef(({ className, ...props }, ref) => {
  const wrapperRef = React.useRef(null);
  const [edges, setEdges] = React.useState({ left: false, right: false });
  const [isMobile, setIsMobile] = React.useState(false);

  // Track viewport breakpoint.  matchMedia is cheap and fires on actual
  // breakpoint crossings — far better than re-rendering on every resize.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const updateEdges = React.useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    if (!hasOverflow) {
      setEdges({ left: false, right: false });
      return;
    }
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setEdges({ left: !atStart, right: !atEnd });
  }, []);

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    // Catch async render of rows + dynamic content changes (filter, add row).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateEdges) : null;
    if (ro) {
      ro.observe(el);
      const tbl = el.querySelector("table");
      if (tbl) ro.observe(tbl);
    }
    // One extra tick — covers the case where the table content paints just
    // after mount and the initial measurement happens too early.
    const t = window.setTimeout(updateEdges, 120);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
      ro && ro.disconnect();
      window.clearTimeout(t);
    };
  }, [updateEdges, isMobile]);

  // Build the dynamic mask gradient.  The stops collapse to {0%, 100%} on the
  // edges that don't need a fade, so the mask is effectively a no-op there.
  let maskStyle;
  if (isMobile && (edges.left || edges.right)) {
    const leftStop = edges.left ? "8%" : "0%";
    const rightStop = edges.right ? "92%" : "100%";
    const grad = `linear-gradient(to right, transparent 0%, #000 ${leftStop}, #000 ${rightStop}, transparent 100%)`;
    maskStyle = { maskImage: grad, WebkitMaskImage: grad };
  }

  return (
    <div
      ref={wrapperRef}
      className="relative w-full overflow-auto"
      style={maskStyle}
    >
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props} />
    </div>
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
